import React, { useState, useEffect, useCallback } from 'react';
import Head from 'next/head';
import { detect } from 'detect-browser';

import { PositionContainer, MediaContainer } from './Unbuilt.styled';

import Wrapper from '@components/DreamToReality/Unbuilt/Wrapper';
import CtaButton from '@components/DreamToReality/Unbuilt/CtaButton';
import ProgressNav from '@components/DreamToReality/Unbuilt/ProgressNav';
import Curtain from '@components/DreamToReality/Unbuilt/Curtain';
import Intro from '@components/DreamToReality/Unbuilt/Intro';
import Outro from '@components/DreamToReality/Unbuilt/Outro';
import VideoControllerFull from '@components/DreamToReality/Unbuilt/VideoControllerFull';
import VideoControllerSplit from '@components/DreamToReality/Unbuilt/VideoControllerSplit';
import VideoControllerStatic from '@components/DreamToReality/Unbuilt/VideoControllerStatic';
import ContentContainer from '@components/DreamToReality/Unbuilt/ContentContainer';
import { pushGTMEvent } from 'pubweb-smokey/dist/utils/analytics';
import { getSiteUrl } from '@utils/config';

import data from '@components/DreamToReality/Unbuilt/Unbuilt.json';

const UP_ARROW_KEYCODE = 38;
const DOWN_ARROW_KEYCODE = 40;

const Unbuilt = () => {
  const [touchStart, setTouchStart] = useState(null);
  const [touchCount, setTouchCount] = useState(0);
  const [videos, setVideos] = useState(null);

  const [activeIndex, setActiveIndex] = useState(0);
  const [playing, setPlaying] = useState(false); // primarily used to prevent scroll pooling during nav events
  const [showCurtain, setShowCurtain] = useState(false);
  const [browser, setBrowser] = useState(null);
  const [showVideo, setShowVideo] = useState(false);

  const onWheel = (e) => {
    // removed the scroll count check to not count to 5 so that the user doesn't have to spin the mouse wheel several times to go to the next slide

    if (e.deltaY > 0 && activeIndex < data.length - 1) {
      navNext();
    } else if (e.deltaY < 0 && activeIndex > 0) {
      navPrev();
    }
  };

  const onKeyDown = useCallback(
    (e) => {
      if (e.keyCode === DOWN_ARROW_KEYCODE && activeIndex < data.length - 1) {
        navNext();
      }

      if (e.keyCode === UP_ARROW_KEYCODE && activeIndex > 0) {
        navPrev();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeIndex]
  );

  const onTouchStart = (e) => {
    setTouchStart(e.touches[0].clientY);
  };

  const onTouchMove = (e) => {
    setTouchCount(touchCount + 1);
    if (touchCount < 5) return;
    setTouchCount(0);

    if (e.touches[0].clientY < touchStart && activeIndex < data.length - 1) {
      navNext();
    } else if (e.touches[0].clientY > touchStart && activeIndex > 0) {
      navPrev();
    }
  };

  const navClick = (item, index) => {
    setActiveIndex(index);

    setShowCurtain(true);
    setPlaying(true);
    setShowVideo(false);

    window.dataLayer.push({
      page: 'unbuilt',
      event: 'nav-click',
      index: index,
    });

    const curtain = document.getElementById('unbuilt-curtain');
    curtain.addEventListener('transitionend', hideCurtain);
  };

  const navPrev = () => {
    const prevIndex = activeIndex - 1;
    setActiveIndex(prevIndex);

    setShowCurtain(true);
    setPlaying(true);
    setShowVideo(false);

    window.dataLayer.push({
      page: 'unbuilt',
      event: 'nav-prev',
      index: prevIndex,
    });

    const curtain = document.getElementById('unbuilt-curtain');
    curtain.addEventListener('transitionend', hideCurtain);
  };

  const navNext = () => {
    const nextIndex = activeIndex + 1;
    setActiveIndex(nextIndex);
    setShowCurtain(false);
    setPlaying(true);
    setShowVideo(true);

    window.dataLayer.push({
      page: 'unbuilt',
      event: 'nav-next',
      index: nextIndex,
    });
  };

  const hideCurtain = (event) => {
    const curtain = document.getElementById('unbuilt-curtain');
    curtain.removeEventListener('transitionend', hideCurtain);

    setTimeout(() => {
      setShowCurtain(false);
      setPlaying(false);
      setShowVideo(false);

      // rewinds videos after any secondary nav performed
      for (const video in videos) {
        // eslint-disable-next-line no-prototype-builtins
        if (videos.hasOwnProperty(video)) {
          videos[video].load();
        }
      }
    }, event.elapsedTime * 1000);
  };

  const videoEnded = () => {
    setTimeout(() => {
      setPlaying(false);
      setShowVideo(false);
    }, 500); // extra buffer to prevent accidental double scroll around short videos
  };

  const reset = () => {
    window.dataLayer.push({
      page: 'unbuilt',
      event: 'button-click',
      label: 'restart',
    });

    // safari has playback issues after being reset, forcing refresh results in better overall experience
    if (browser?.name === 'safari' || browser?.name === 'ios') {
      window.location.reload();
      return;
    } else {
      setActiveIndex(0);
      setPlaying(false);
      setShowCurtain(false);
    }

    for (const video in videos) {
      // eslint-disable-next-line no-prototype-builtins
      if (videos.hasOwnProperty(video)) {
        videos[video].load();
      }
    }
  };

  useEffect(() => {
    setBrowser(detect());

    document.body.style.overflow = 'hidden';
    setVideos(document.getElementsByClassName('unbuilt-video'));

    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onKeyDown]);

  let media;
  let skipDelay = false;

  // originally this checked for chrome ONLY if it's on Android OS - this prevented the video from playing on Chrome for Windows....
  if (
    (browser?.name === 'chrome' && browser?.os !== 'Mac OS') ||
    browser?.name === 'firefox' ||
    browser?.name === 'ie' ||
    browser?.name === 'edge'
  ) {
    media = (
      <VideoControllerFull
        data={data}
        activeIndex={activeIndex}
        videoEnded={videoEnded}
        showVideo={showVideo}
      />
    );
  } else if (
    (browser?.name === 'chrome' && browser?.os === 'Mac OS') ||
    browser?.name === 'safari' ||
    browser?.name === 'ios'
  ) {
    media = (
      <VideoControllerSplit
        data={data}
        activeIndex={activeIndex}
        videoEnded={videoEnded}
        showVideo={showVideo}
      />
    );
  } else {
    skipDelay = true;

    media = (
      <VideoControllerStatic
        data={data}
        activeIndex={activeIndex}
        videoEnded={videoEnded}
        showVideo={showVideo}
      />
    );
  }

  return (
    <Wrapper>
      <Head>
        <title>Unbuilt | Clayton Homes</title>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no"
        />
        <meta name="referrer" content="always" />
        <meta charSet="utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta
          property="og:image"
          content={`${getSiteUrl()}/public-assets/images/unbuilt/ogimage-unbuilt.jpg`}
        />
      </Head>
      <PositionContainer
        id="unbuilt-positioncontainer"
        onWheel={playing ? undefined : (e) => onWheel(e)}
        onTouchStart={playing ? undefined : (e) => onTouchStart(e)}
        onTouchMove={playing ? undefined : (e) => onTouchMove(e)}
      >
        <Curtain show={showCurtain} />

        <CtaButton
          pristine={activeIndex === 1}
          visible={activeIndex > 0 && activeIndex < 13 ? true : false}
          onClick={() => {
            pushGTMEvent('ch.cta_click', 'dtr_unbuilt_view_homes', {
              location: 'Unbuilt Page',
              category: 'home_search',
              action: 'button',
              label: 'dtr_unbuilt_view_homes',
            });
          }}
        />

        <ProgressNav
          data={data}
          navClick={navClick}
          activeIndex={activeIndex}
          pristine={activeIndex === 1}
          visible={activeIndex > 0 && activeIndex < 13 ? true : false}
        />

        <MediaContainer id="unbuilt-mediacontainer">{media}</MediaContainer>

        <Intro data={data[0]} visible={activeIndex === 0} />

        <Outro
          data={data[13]}
          visible={activeIndex === 13}
          playing={playing}
          reset={reset}
        />

        <ContentContainer
          data={data}
          activeIndex={activeIndex}
          showVideo={showVideo}
          navPrev={navPrev}
          navNext={navNext}
          skipDelay={skipDelay}
        />
      </PositionContainer>
    </Wrapper>
  );
};

export default Unbuilt;
